import { useEffect, useState } from "react";
import { Moon, CheckCircle, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";

const LOG_LINES = [
  "02:14 ✅ Scan: yourdomain.com (score 94/100)",
  "02:17 🎯 Found 3 new ranking opportunities",
  "02:23 ✍️ Draft blog: '2026 SEO trends'",
  "02:31 🔧 Fixed 14 broken links",
  "02:45 ⚠️ Competitor jumped to #4 — alert sent",
  "02:58 📊 Schema markup added to 8 product pages",
  "03:01 📈 Weekly report compiled",
  "03:12 🚀 Meta tags updated on 12 pages (CTR -12%)",
];

const SleepAISection = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [visibleLogs, setVisibleLogs] = useState([]);
  const isBusiness = ["business", "enterprise"].includes((user?.plan || "").toLowerCase());

  // Typewriter-like reveal loop
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      while (!cancelled) {
        setVisibleLogs([]);
        for (let i = 0; i < LOG_LINES.length; i++) {
          if (cancelled) return;
          setVisibleLogs((prev) => [...prev, LOG_LINES[i]]);
          await new Promise((r) => setTimeout(r, 1400));
        }
        await new Promise((r) => setTimeout(r, 4000));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const improvements = [
    t.sleep_check_1 || "Scans 100% of your pages",
    t.sleep_check_2 || "Checks 47 AI platforms (ChatGPT, Perplexity, Claude…)",
    t.sleep_check_3 || "Generates 5 new optimization recommendations",
    t.sleep_check_4 || "Sends an alert if a competitor overtakes your position",
    t.sleep_check_5 || "Drafts a blog article for tomorrow",
    t.sleep_check_6 || "Adjusts meta tags where CTR dropped",
  ];

  return (
    <section
      id="sleep-ai"
      data-testid="sleep-ai-section"
      className="relative py-24 overflow-hidden themed-section"
      style={{
        background: "linear-gradient(180deg, var(--color-bg) 0%, var(--color-surface-alt) 50%, var(--color-bg) 100%)",
      }}
    >
      <div className="absolute inset-0 opacity-30">
        <div className="absolute left-1/4 top-1/3 w-[400px] h-[400px] rounded-full blur-[120px]"
          style={{ background: "var(--color-primary)" }}
        />
        <div className="absolute right-1/4 bottom-1/3 w-[400px] h-[400px] rounded-full blur-[120px]"
          style={{ background: "var(--color-accent)" }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left — Copy */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full border"
            style={{
              background: "color-mix(in srgb, var(--color-surface) 80%, transparent)",
              borderColor: "var(--color-border)",
            }}
          >
            <Moon className="w-3.5 h-3.5" style={{ color: "var(--color-accent)" }} />
            <span
              className="text-[10px] font-mono uppercase tracking-widest"
              style={{ color: "var(--color-accent)" }}
            >
              {t.sleep_tag || "Automation 24/7"}
            </span>
          </div>

          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-6"
            style={{ color: "var(--color-text)" }}
          >
            {t.sleep_title_a || "While you sleep,"}{" "}
            <span
              className="bg-gradient-to-r bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
              }}
            >
              {t.sleep_title_b || "PulseAI works"}
            </span>
          </h2>

          <p
            className="text-lg mb-8 leading-relaxed"
            style={{ color: "var(--color-text-muted)" }}
          >
            {t.sleep_subtitle || "Every night at 3:00 AM your AI assistant automatically runs 6 critical tasks. You open your email in the morning —"}{" "}
            <span className="font-bold" style={{ color: "var(--color-text)" }}>
              {t.sleep_highlight || "17 new improvements waiting for 1-click approval."}
            </span>
          </p>

          <div className="space-y-2 mb-8">
            {improvements.map((line, idx) => (
              <div key={idx} className="flex items-start gap-2.5">
                <CheckCircle
                  className="w-5 h-5 shrink-0 mt-0.5"
                  style={{ color: "var(--color-primary)" }}
                />
                <span style={{ color: "var(--color-text)" }}>{line}</span>
              </div>
            ))}
          </div>

          <a
            href={isBusiness ? "/dashboard" : "#pricing"}
            data-testid="sleep-ai-cta"
            className="inline-flex items-center gap-2 px-6 py-3 font-bold rounded-lg transition-transform hover:scale-105"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-bg)",
            }}
          >
            {isBusiness ? "Open my Sleep AI dashboard" : "🔒 Unlock with Business (€169/mo)"} →
          </a>
          {!isBusiness && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 text-xs"
              style={{ color: "var(--color-text-muted)" }}
            >
              <Lock className="w-3 h-3" />
              <span>Sleep AI automation is Business & Enterprise only</span>
            </div>
          )}
        </div>

        {/* Right — AI Activity Log */}
        <div
          className="relative rounded-2xl overflow-hidden border shadow-2xl"
          data-testid="ai-activity-log"
          style={{
            background: "var(--color-surface-alt)",
            borderColor: "var(--color-border)",
          }}
        >
          <div
            className="px-4 py-3 flex items-center justify-between border-b"
            style={{ borderColor: "var(--color-border)" }}
          >
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="w-3 h-3 rounded-full bg-emerald-500" />
            </div>
            <span
              className="text-[10px] font-mono uppercase tracking-widest"
              style={{ color: "var(--color-text-muted)" }}
            >
              pulseai.log · live
            </span>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          </div>
          <div className="p-5 font-mono text-sm min-h-[320px] space-y-2">
            {visibleLogs.map((line, idx) => (
              <div
                key={idx}
                className="opacity-0 animate-fade-in"
                style={{
                  color: "var(--color-text)",
                  animation: "fadeInLog 0.6s ease-out forwards",
                }}
              >
                <span style={{ color: "var(--color-text-muted)" }}>›</span> {line}
              </div>
            ))}
            <span
              className="inline-block w-2 h-4 align-middle animate-pulse"
              style={{ background: "var(--color-primary)" }}
            />
          </div>
          <div
            className="px-5 py-3 border-t text-center"
            style={{
              borderColor: "var(--color-border)",
              background: "color-mix(in srgb, var(--color-surface) 50%, transparent)",
            }}
          >
            <p className="text-sm font-bold" style={{ color: "var(--color-primary)" }}>
              ⚡ {t.sleep_overnight || "Overnight improvements:"} <span className="font-black text-xl">17</span> {t.sleep_pending || "waiting for approval"}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInLog {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default SleepAISection;
